import React, { useEffect, useState } from 'react';
import NodeModal from '../../components/Node/NewNode';
import { apiFetch, getApiBase, buildHeaders } from '../../lib/api';
import './NodePage.css';

const API_BASE = getApiBase();

const getAlertBadge = (level) => {
  if (level == null || level <= 1) return { cls: 'status-healthy', text: 'Healthy' };
  if (level <= 3) return { cls: 'status-warning', text: 'Warning' };
  return { cls: 'status-critical', text: 'Critical' };
};

const formatDate = (dateValue) => {
  if (!dateValue) return '—';
  return new Date(dateValue).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatSync = (dateValue) => {
  if (!dateValue) return 'Never synced';

  const minutes = Math.round((Date.now() - new Date(dateValue).getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.round(minutes / 60)}h ago`;
};

const formatNodeId = (node) => {
  if (node?.node_code) return node.node_code;
  if (!node?.id) return '—';
  return String(node.id).slice(0, 8).toUpperCase();
};

const getCreatedBy = (node) => node?.created_by_display || node?.created_by_name || 'System Admin';

const formatCoordinate = (value) => {
  if (value == null || value === '') return '—';
  return value;
};

const formatTemperature = (value) => {
  if (value == null || value === '') return '—';
  return `${value}°C`;
};

const formatAnomaly = (value) => {
  if (value == null || value === '') return '—';
  return `${value > 0 ? '+' : ''}${value}°C`;
};

const ActivityMetricCard = ({ label, value, valueClassName = '' }) => (
  <div className="activity-metric-card">
    <span>{label}</span>
    <strong className={valueClassName}>{value}</strong>
  </div>
);

const NodeDetailRow = ({ label, value, valueClassName = '' }) => (
  <div className="node-detail-row">
    <span>{label}</span>
    <strong className={valueClassName}>{value}</strong>
  </div>
);

const NodeActivityModal = ({ node, isOpen, onClose }) => {
  if (!isOpen || !node) return null;

  const { cls: badgeClass, text: badgeText } = getAlertBadge(node.crw_alert_level);

  return (
    <div className="node-activity-overlay" role="presentation">
      <div
        className="node-activity-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="node-activity-title"
      >
        <div className="node-activity-header">
          <div className="node-activity-header-copy">
            <span className="node-activity-kicker">{formatNodeId(node)}</span>
            <h2 id="node-activity-title">{node.name || 'Unnamed Node'}</h2>
            <p>{node.region || '—'}</p>
          </div>
          <div className="node-activity-header-actions">
            <span className={`status-badge ${badgeClass}`}>{badgeText}</span>
          </div>
        </div>

        <div className="node-activity-section">
          <h4>Environmental Snapshot</h4>
          <div className="node-activity-grid">
            <ActivityMetricCard label="Sea Surface Temp" value={formatTemperature(node.crw_sst)} />
            <ActivityMetricCard
              label="Anomaly"
              value={formatAnomaly(node.crw_sst_anomaly)}
              valueClassName={node.crw_sst_anomaly > 0 ? 'metric-warning' : node.crw_sst_anomaly < 0 ? 'metric-healthy' : ''}
            />
            <ActivityMetricCard label="Stress Level / DHW" value={node.crw_dhw ?? '—'} />
            <ActivityMetricCard label="NOAA Synced" value={formatSync(node.crw_last_synced || node.last_sync)} />
          </div>
        </div>

        <div className="node-activity-section">
          <h4>Node Details</h4>
          <div className="node-details-grid">
            <NodeDetailRow label="Latitude" value={formatCoordinate(node.latitude)} />
            <NodeDetailRow label="Longitude" value={formatCoordinate(node.longitude)} />
            <NodeDetailRow label="Created Date" value={formatDate(node.created_at)} />
            <NodeDetailRow label="Created By" value={getCreatedBy(node)} />
            <NodeDetailRow
              label="Status"
              value={node.is_active !== false ? 'Active' : 'Deactivated'}
              valueClassName={node.is_active !== false ? 'metric-healthy' : ''}
            />
          </div>
        </div>

        <div className="activity-modal-footer">
          <button type="button" className="node-secondary-action" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const NodesControlPage = () => {
  const [nodes, setNodes] = useState([]);
  const [filter, setFilter] = useState('All');
  const [counts, setCounts] = useState({ all: 0, healthy: 0, warning: 0, critical: 0 });
  const [loadingNodes, setLoadingNodes] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedNodeData, setSelectedNodeData] = useState({});
  const [savingNode, setSavingNode] = useState(false);

  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [activityNode, setActivityNode] = useState(null);

  const fetchNodes = async () => {
    try {
      const data = await apiFetch('/api/nodes/');
      const sorted = [...data].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      setNodes(sorted);

      let healthy = 0;
      let warning = 0;
      let critical = 0;

      sorted.forEach((node) => {
        const level = node.crw_alert_level;
        if (level == null || level <= 1) healthy += 1;
        else if (level <= 3) warning += 1;
        else critical += 1;
      });

      setCounts({
        all: sorted.length,
        healthy,
        warning,
        critical,
      });
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoadingNodes(false);
    }
  };

  useEffect(() => {
    fetchNodes();
  }, []);

  const handleSyncNOAA = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_BASE}/api/nodes/sync-noaa/all`, {
        method: 'POST',
        headers: buildHeaders(),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.detail || result?.message || 'NOAA sync failed');
      }

      const errors = (result.results || [])
        .filter((item) => item?.error)
        .map((item) => item.error);
      const uniqueErrors = [...new Set(errors)];

      const summary =
        result.failed > 0 && uniqueErrors.length > 0
          ? `NOAA sync finished. Synced: ${result.synced}, Failed: ${result.failed}. First error: ${uniqueErrors[0]}`
          : `NOAA sync finished. Synced: ${result.synced}, Failed: ${result.failed}`;

      alert(summary);
      await fetchNodes();
    } catch (error) {
      console.error('NOAA sync error:', error);
      alert(error.message || 'NOAA sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const openCreateModal = () => {
    setSelectedNodeData({
      id: null,
      name: '',
      region: '',
      latitude: '',
      longitude: '',
      is_active: true,
    });
    setModalMode('create');
    setIsModalOpen(true);
  };

  const openEditModal = (node) => {
    setIsActivityOpen(false);
    setActivityNode(null);
    setSelectedNodeData({
      id: node.id,
      name: node.name ?? '',
      region: node.region ?? '',
      latitude: node.latitude ?? '',
      longitude: node.longitude ?? '',
      is_active: node.is_active ?? true,
    });
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const openActivityModal = (node) => {
    setActivityNode(node);
    setIsActivityOpen(true);
  };

  const closeActivityModal = () => {
    setIsActivityOpen(false);
    setActivityNode(null);
  };

  const handleSaveNode = async (formData) => {
    const storedUser = localStorage.getItem('murjan_user');
    const user = storedUser ? JSON.parse(storedUser) : null;
    const latitude = Number(formData.latitude);
    const longitude = Number(formData.longitude);

    if (
      !formData.name?.trim() ||
      !formData.region?.trim() ||
      formData.latitude === '' ||
      formData.longitude === '' ||
      !Number.isFinite(latitude) ||
      !Number.isFinite(longitude)
    ) {
      alert('All node fields are required.');
      return;
    }

    if (latitude < -90 || latitude > 90) {
      alert('Latitude must be between -90 and 90.');
      return;
    }

    if (longitude < -180 || longitude > 180) {
      alert('Longitude must be between -180 and 180.');
      return;
    }

    setSavingNode(true);

    if (modalMode === 'create') {
      try {
        const response = await fetch(`${API_BASE}/api/nodes/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('murjan_token')}`,
          },
          body: JSON.stringify({
            name: formData.name.trim(),
            region: formData.region.trim(),
            latitude,
            longitude,
            created_by_name: user?.full_name || user?.email || 'Unknown',
          }),
        });
        const result = await response.json();

        if (!response.ok) {
          alert(result.detail || 'Failed to create and sync node');
          setSavingNode(false);
          return;
        }

        if (result?.sync?.error) {
          alert(`Node created, but NOAA sync did not return data yet: ${result.sync.error}`);
        }
      } catch (error) {
        console.error('Create node error:', error);
        alert('Failed to create and sync node');
        setSavingNode(false);
        return;
      }
    } else {
      try {
        await apiFetch(`/api/nodes/${formData.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name.trim(),
            region: formData.region.trim(),
            latitude,
            longitude,
          }),
        });
      } catch (error) {
        console.error('Update error:', error);
        alert(error.message || 'Failed to update node');
        setSavingNode(false);
        return;
      }
    }

    setIsModalOpen(false);
    await fetchNodes();
    setSavingNode(false);
  };

  const handleToggleActive = async (formData) => {
    try {
      await apiFetch(`/api/nodes/${formData.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !formData.is_active }),
      });
      setIsModalOpen(false);
      fetchNodes();
    } catch (error) {
      console.error('Toggle error:', error);
      alert(error.message || 'Failed to update node status');
    }
  };

  const displayedNodes = nodes.filter((node) => {
    if (filter === 'All') return true;
    const { text } = getAlertBadge(node.crw_alert_level);
    return text === filter;
  });

  return (
    <div className="nodes-control-wrapper">
      <div className="nodes-control-header">
        <div className="nodes-control-title">
          <h1>Nodes Control</h1>
          <p>Manage coral reef nodes and oversee alert levels</p>
        </div>
        <div className="header-actions">
          <button className="sync-noaa-btn" onClick={handleSyncNOAA} disabled={syncing}>
            {syncing ? '⟳ Syncing...' : '⟳ Sync NOAA'}
          </button>
          <button className="add-nodes-btn" onClick={openCreateModal}>
            + Add new Node
          </button>
        </div>
      </div>

      <div className="nodes-filters">
        {['All', 'Healthy', 'Warning', 'Critical'].map((filterOption) => (
          <button
            key={filterOption}
            className={filter === filterOption ? 'active' : ''}
            onClick={() => setFilter(filterOption)}
          >
            {filterOption} ({filterOption === 'All' ? counts.all : counts[filterOption.toLowerCase()]})
          </button>
        ))}
      </div>

      <div className="nodes-table-container">
        <table className="nodes-table">
          <thead>
            <tr>
              <th>Node ID</th>
              <th>Node Name</th>
              <th>Location</th>
              <th>Alert Level</th>
              <th>Created Date</th>
              <th>Created By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loadingNodes ? (
              <tr>
                <td colSpan="7" className="nodes-loading-state">
                  <span className="table-loading-spinner" aria-hidden="true" />
                  Loading nodes...
                </td>
              </tr>
            ) : displayedNodes.length === 0 ? (
              <tr>
                <td colSpan="7" className="nodes-empty-state">
                  No nodes found for this filter.
                </td>
              </tr>
            ) : (
              displayedNodes.map((node) => {
                const { cls: badgeClass, text: badgeText } = getAlertBadge(node.crw_alert_level);

                return (
                  <tr key={node.id}>
                    <td>{formatNodeId(node)}</td>
                    <td className="node-name-cell">
                      <strong>{node.name || 'Unnamed Node'}</strong>
                    </td>
                    <td className="node-location-cell">{node.region || '—'}</td>
                    <td>
                      <span className={`status-badge ${badgeClass}`}>{badgeText}</span>
                    </td>
                    <td>{formatDate(node.created_at)}</td>
                    <td>{getCreatedBy(node)}</td>
                    <td className="nodes-actions-cell">
                      <button
                        type="button"
                        className="node-icon-action"
                        aria-label={`Edit ${node.name || 'node'}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditModal(node);
                        }}
                      >
                        ✎
                      </button>
                      <button
                        type="button"
                        className="view-activity-action"
                        onClick={(event) => {
                          event.stopPropagation();
                          openActivityModal(node);
                        }}
                      >
                        View Activity
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <NodeActivityModal
        node={activityNode}
        isOpen={isActivityOpen}
        onClose={closeActivityModal}
      />

      <NodeModal
        isOpen={isModalOpen}
        mode={modalMode}
        initialData={selectedNodeData}
        isSaving={savingNode}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveNode}
        onToggleActive={handleToggleActive}
      />
    </div>
  );
};

export default NodesControlPage;
